import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box } from '@mui/material';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';

// Import i18n configuration
import './i18n';

// Core components (immediate load for security)
import Login from '@/pages/Login';
import LoginHosted from '@/pages/LoginHosted';
import { Layout } from '@/components/layout';

// Lazy loaded page components
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Content = lazy(() => import('@/pages/Content'));
const ContentDetail = lazy(() => import('@/pages/ContentDetail'));
const Settings = lazy(() => import('@/pages/Settings'));
const PasswordChangePage = lazy(() => import('@/pages/PasswordChangePage'));

// Document page components (lazy loaded)
const DocumentView = lazy(() => import('@/pages/DocumentView'));
const DocumentEditor = lazy(() => import('@/pages/DocumentEditor'));

// Admin page components (lazy loaded after permission check)
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const AdminContentManagement = lazy(() => import('@/pages/AdminContentManagement'));
const ContentEditPage = lazy(() => import('@/pages/ContentEditPage'));

// Optional page components (maximum lazy loading)
const Analytics = lazy(() => import('@/pages/Analytics'));
const UserManagement = lazy(() => import('@/pages/UserManagement'));

// Import hooks and services (immediate load for security)
import { AuthProvider, useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';

// Import Amplify configuration
import amplifyConfig from '@/config/amplify';

// Configure Amplify with imported config
Amplify.configure(amplifyConfig);

// Debug logging for development (only in debug mode)
if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_MODE === 'true') {
  console.log('App Configuration:', {
    environment: import.meta.env.VITE_ENVIRONMENT,
    userPoolId: amplifyConfig.Auth.Cognito.userPoolId,
    clientId: amplifyConfig.Auth.Cognito.userPoolClientId,
    region: import.meta.env.VITE_AWS_REGION,
    debugMode: import.meta.env.VITE_DEBUG_MODE,
  });
}

// Create Material-UI theme
const createAppTheme = (mode: 'light' | 'dark' | 'auto') => {
  const actualMode = mode === 'auto' 
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : mode;
    
  return createTheme({
    palette: {
      mode: actualMode,
      primary: {
        main: '#1976d2',
        light: '#42a5f5',
        dark: '#1565c0',
      },
      secondary: {
        main: '#dc004e',
        light: '#ff5983',
        dark: '#9a0036',
      },
      background: {
        default: actualMode === 'light' ? '#f5f5f5' : '#121212',
        paper: actualMode === 'light' ? '#ffffff' : '#1e1e1e',
      },
    },
    typography: {
      fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(','),
      h1: {
        fontSize: '2.5rem',
        fontWeight: 600,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 600,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 600,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 600,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 600,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 600,
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          },
        },
      },
    },
  });
};

// Loading component
const LoadingSpinner: React.FC = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress size={60} />
  </Box>
);

// Protected Route component
interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [] 
}) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check role-based access if required roles are specified
  if (requiredRoles.length > 0 && user) {
    const hasRequiredRole = requiredRoles.includes(user.role);
    if (!hasRequiredRole) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

// App Content component (needs to be inside AuthProvider)
const AppContent: React.FC = () => {
  const { themeMode } = useTheme();
  const theme = React.useMemo(() => createAppTheme(themeMode), [themeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/login-hosted" element={<LoginHosted />} />
            
            {/* OAuth callback route */}
            <Route path="/auth/callback" element={<Login />} />
            <Route path="/auth/logout" element={<Login />} />
            
            {/* Protected routes with layout */}
            <Route path="/" element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="content" element={<Content />} />
              <Route path="content/:id" element={<ContentDetail />} />
              
              {/* Content Edit Route - Admin/Manager/Owner only */}
              <Route path="content/edit/:id" element={
                <ProtectedRoute requiredRoles={['admin', 'manager']}>
                  <ContentEditPage />
                </ProtectedRoute>
              } />
              
              {/* Document Routes - accessible from Content page */}
              <Route path="documents/:documentId" element={<DocumentView />} />
              <Route path="documents/:documentId/edit" element={
                <ProtectedRoute requiredRoles={['admin', 'manager']}>
                  <DocumentEditor />
                </ProtectedRoute>
              } />
              <Route path="documents/new" element={
                <ProtectedRoute requiredRoles={['admin', 'manager']}>
                  <DocumentEditor />
                </ProtectedRoute>
              } />
              
              <Route path="settings" element={<Settings />} />
              <Route path="password-change" element={<PasswordChangePage />} />
              
              {/* Admin routes - Under Development */}
              <Route path="admin" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <Box 
                    display="flex" 
                    flexDirection="column" 
                    justifyContent="center" 
                    alignItems="center" 
                    minHeight="60vh"
                    textAlign="center"
                    p={3}
                  >
                    <CircularProgress size={40} sx={{ mb: 2 }} />
                    <h2>管理者ページ</h2>
                    <p>現在開発中です。しばらくお待ちください。</p>
                  </Box>
                </ProtectedRoute>
              } />
              <Route path="admin/content" element={
                <ProtectedRoute requiredRoles={['admin']}>
                  <AdminContentManagement />
                </ProtectedRoute>
              } />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </ThemeProvider>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
